/**
 * Fuente única de verdad para el alcance de datos según el rol del usuario.
 *
 * Reglas:
 *  - administrador → acceso total (isAll = true).
 *  - inspector     → tiendas/clientes donde tienda.inspectorCod === inspectorCodFreematica.
 *  - administrativo con delegaciones → tiendas/clientes cuya delegacion ∈
 *    delegacionesAsignadas. Si además hay `empresasAsignadas`, se exige AND con
 *    `codEmpresa ∈ empresasAsignadas` (vacío = sin restricción de empresa).
 *  - Las delegaciones de un inspector NO amplían sus tiendas/clientes (solo
 *    aplican a vigilantes); sus tiendas/clientes salen siempre de inspectorCod
 *    (el filtro por empresa NO se aplica a las tiendas derivadas de inspectorCod).
 *  - vigilantes para inspector y administrativo se filtran SIEMPRE por delegación
 *    (el inspectorCod no aplica a vigilantes). Si además hay `empresasAsignadas`,
 *    se exige AND con `empresa ∈ empresasAsignadas` (vacío = sin restricción de empresa).
 *    Igualmente, si hay `departamentosAsignados`, se exige AND con
 *    `departamentoFreematica ∈ departamentosAsignados` (array vacío = sin
 *    restricción). EXCEPCIÓN: los vigilantes SIN departamento informado son
 *    visibles para todos los que cumplan empresa/delegación, no se ocultan.
 *  - El filtro por empresa aplica a tiendas/clientes (rama administrativo por
 *    delegación) y a vigilantes. El filtro por departamento solo aplica a
 *    vigilantes (las tiendas no tienen departamento).
 *  - Combinación de roles: unión (OR).
 *  - Campos legacy (clientesAsignados, tiendasAsignadas, provinciasAsignadas,
 *    comunidadesAsignadas, arrays administrativos/inspectores en cliente/tienda)
 *    se ignoran.
 */

const ADMIN = 'administrador';
const ADMINISTRATIVO = 'administrativo';
const INSPECTOR = 'inspector';

const toRoles = (adminData) => {
  const r = adminData?.tipoUsuario;
  if (Array.isArray(r)) return r;
  if (typeof r === 'string' && r) return [r];
  return [];
};

/**
 * Extrae los códigos de inspector Freematica asignados a un usuario.
 *
 * Soporta el modelo nuevo multi-inspector y los campos legacy de un único
 * inspector, para que los documentos antiguos sigan funcionando:
 *  - `inspectoresFreematica`: array de objetos `{ cod, nombre }` (modelo actual).
 *  - `inspectorCodFreematica`: string único (modelo legacy).
 *
 * @param {Object|null|undefined} adminData
 * @returns {string[]} códigos únicos, sin vacíos.
 */
export function getInspectorCods(adminData) {
  let cods = [];
  if (Array.isArray(adminData?.inspectoresFreematica)) {
    cods = adminData.inspectoresFreematica.map((i) =>
      i && i.cod != null ? String(i.cod).trim() : ''
    );
  } else if (adminData?.inspectorCodFreematica) {
    cods = [String(adminData.inspectorCodFreematica).trim()];
  }
  return [...new Set(cods.filter(Boolean))];
}

/**
 * Devuelve un scope normalizado a partir del adminData del AuthContext.
 *
 * @param {Object|null|undefined} adminData
 * @returns {{
 *   isAll: boolean,
 *   roles: string[],
 *   delegaciones: string[],
 *   empresas: string[],
 *   departamentos: string[],
 *   inspectorCod: string|null,
 *   inspectorCods: string[],
 *   isInspector: boolean,
 *   isAdministrativo: boolean,
 * }}
 */
export function getUserScope(adminData) {
  const roles = toRoles(adminData);
  const isAll = roles.includes(ADMIN);
  const isInspector = roles.includes(INSPECTOR);
  const isAdministrativo = roles.includes(ADMINISTRATIVO);

  const delegacionesRaw = Array.isArray(adminData?.delegacionesAsignadas)
    ? adminData.delegacionesAsignadas
    : [];
  const delegaciones = delegacionesRaw
    .map((d) => (d == null ? '' : String(d).trim()))
    .filter(Boolean);

  const empresasRaw = Array.isArray(adminData?.empresasAsignadas)
    ? adminData.empresasAsignadas
    : [];
  const empresas = empresasRaw
    .map((e) => (e == null ? '' : String(e).trim()))
    .filter(Boolean);

  const departamentosRaw = Array.isArray(adminData?.departamentosAsignados)
    ? adminData.departamentosAsignados
    : [];
  const departamentos = departamentosRaw
    .map((d) => (d == null ? '' : String(d).trim()))
    .filter(Boolean);

  const inspectorCods = isInspector ? getInspectorCods(adminData) : [];
  const inspectorCod = inspectorCods.length > 0 ? inspectorCods[0] : null;

  return {
    isAll,
    roles,
    delegaciones,
    empresas,
    departamentos,
    inspectorCod,
    inspectorCods,
    isInspector,
    isAdministrativo,
  };
}

/**
 * Firma estable del scope efectivo de un usuario: rol + delegaciones +
 * empresas + departamentos + inspectores (todos ordenados).
 *
 * Se usa como parte de la CLAVE de las cachés de scope (scopeData,
 * firestoreService). Así, cuando un administrador cambia los permisos de un
 * usuario (EditAdmin), la clave cambia y la siguiente lectura trae datos
 * frescos AL INSTANTE, en vez de servir el resultado cacheado del scope
 * anterior durante el TTL (antes: hasta 5 min de permisos desfasados).
 *
 * @param {Object|null|undefined} adminData
 * @returns {string}
 */
export function scopeSignature(adminData) {
  const scope = getUserScope(adminData);
  if (scope.isAll) return 'all';
  const parts = [
    scope.roles.slice().sort().join(','),
    scope.delegaciones.slice().sort().join(','),
    scope.empresas.slice().sort().join(','),
    scope.departamentos.slice().sort().join(','),
    scope.inspectorCods.slice().sort().join(','),
  ];
  return parts.join('|');
}

/**
 * ¿La tienda está dentro del scope?
 * Combinación: el usuario la ve si CUALQUIERA de sus roles la incluye.
 *
 * Para el administrativo, además de la delegación se exige (AND) que la tienda
 * pertenezca a una de las `empresasAsignadas` cuando las haya (vacío = sin
 * restricción de empresa). El campo de empresa en la tienda es `codEmpresa`
 * (lo escribe el sync de servicios). El filtro por empresa NO se aplica a las
 * tiendas derivadas de inspectorCod (rama inspector).
 */
export function tiendaVisible(tienda, scope) {
  if (!scope || scope.isAll) return true;
  if (!tienda) return false;

  if (scope.isInspector && scope.inspectorCods.length > 0) {
    const cod = tienda.inspectorCod ? String(tienda.inspectorCod) : '';
    if (cod && scope.inspectorCods.includes(cod)) return true;
  }

  // Las delegaciones solo amplían el scope de tiendas para administrativos.
  // Para inspector, las delegaciones aplican únicamente a vigilantes; sus
  // tiendas/clientes se derivan de inspectorCod.
  if (scope.isAdministrativo && scope.delegaciones.length > 0) {
    const del = tienda.delegacion ? String(tienda.delegacion) : '';
    if (del && scope.delegaciones.includes(del)) {
      // AND por empresa: si hay empresas asignadas, la tienda debe pertenecer
      // a una de ellas (mismo criterio que los vigilantes).
      if (scope.empresas.length === 0) return true;
      const emp = tienda.codEmpresa ? String(tienda.codEmpresa).trim() : '';
      return !!(emp && scope.empresas.includes(emp));
    }
  }

  return false;
}

/**
 * ¿El vigilante está dentro del scope?
 * Para inspector y administrativo se filtra siempre por delegación; si además
 * hay empresas y/o departamentos asignados, se exige AND con
 * `empresa ∈ empresasAsignadas` y `departamentoFreematica ∈ departamentosAsignados`.
 * Cada array vacío desactiva su filtro correspondiente. Los vigilantes sin
 * departamento informado quedan exentos del filtro de departamento.
 */
export function vigilanteVisible(vigilante, scope) {
  if (!scope || scope.isAll) return true;
  if (!vigilante) return false;

  if ((scope.isAdministrativo || scope.isInspector) && scope.delegaciones.length > 0) {
    const del = vigilante.delegacion ? String(vigilante.delegacion) : '';
    if (!(del && scope.delegaciones.includes(del))) return false;

    if (scope.empresas.length > 0) {
      // `codEmpresa` es el único campo de empresa en docs de vigilante
      // (unificación 2026-06-04; el legacy `empresa` se eliminó).
      const emp = vigilante.codEmpresa ? String(vigilante.codEmpresa) : '';
      if (!(emp && scope.empresas.includes(emp))) return false;
    }

    if (scope.departamentos.length > 0) {
      // `departamentoFreematica` es el campo de departamento (PERHH_DPTO) que
      // escribe el sync en cada doc de vigilante.
      // Vigilante SIN departamento → visible para todos (ya cumple
      // empresa/delegación); solo se exige pertenencia cuando el doc TIENE
      // departamento informado.
      const dpto = vigilante.departamentoFreematica ? String(vigilante.departamentoFreematica) : '';
      if (dpto && !scope.departamentos.includes(dpto)) return false;
    }

    return true;
  }

  return false;
}

/**
 * ¿La solicitud de cambio de turno está dentro del scope del usuario?
 *
 * Usa los campos denormalizados en el propio doc de la solicitud
 * (`inspectorCods`, `delegacion`, `codEmpresa`, `departamentoFreematica`), sin
 * leer el doc del vigilante:
 *  - administrador → todas.
 *  - inspector → las de sus inspectorCods; si ninguno casa, se cae al criterio
 *    de delegación (cubre al solicitante sin tienda con inspectorCod).
 *  - administrativo → por delegación + empresa + departamento.
 *
 * Compartida por la página /cambios-turno y el contador del menú lateral, para
 * que el badge nunca cuente solicitudes que el usuario no puede abrir.
 *
 * @param {Object} s Doc de `solicitudesCambioTurno`.
 * @param {Object} scope Resultado de `getUserScope`.
 * @return {boolean} true si el usuario puede verla.
 */
export function cambioTurnoVisible(s, scope) {
  if (!scope || scope.isAll) return true;
  if (!s) return false;

  if (scope.isInspector && Array.isArray(s.inspectorCods)
    && s.inspectorCods.some((c) => scope.inspectorCods.includes(String(c)))) {
    return true;
  }

  if (scope.isAdministrativo || scope.isInspector) {
    return vigilanteVisible({
      delegacion: s.delegacion,
      codEmpresa: s.codEmpresa,
      departamentoFreematica: s.departamentoFreematica,
    }, scope);
  }

  return false;
}

/**
 * ¿El registro de acceso está dentro del scope?
 *
 * Misma lógica que `vigilanteVisible` pero aplicada a un registro de la
 * colección `accesos` (historial de logins):
 *  - administrador → ve todos los accesos.
 *  - inspector/administrativo → solo los de sus delegaciones; si además tiene
 *    empresas asignadas, se exige AND con `codEmpresa ∈ empresasAsignadas`.
 *
 * Los accesos SIN delegación informada (típicamente logins de admins al
 * dashboard, que no pertenecen a una empresa/delegación) NO son visibles para
 * inspector/administrativo — solo para el administrador.
 */
export function accesoVisible(acceso, scope) {
  if (!scope || scope.isAll) return true;
  if (!acceso) return false;

  if ((scope.isAdministrativo || scope.isInspector) && scope.delegaciones.length > 0) {
    const del = acceso.delegacion ? String(acceso.delegacion) : '';
    if (!(del && scope.delegaciones.includes(del))) return false;

    if (scope.empresas.length > 0) {
      const emp = acceso.codEmpresa ? String(acceso.codEmpresa) : '';
      if (!(emp && scope.empresas.includes(emp))) return false;
    }

    return true;
  }

  return false;
}

/**
 * ¿El clienteId está dentro del scope, dada la lista de tiendas accesibles?
 * Los clientes "visibles" son los de las tiendas accesibles.
 */
export function clienteVisible(clienteId, scope, tiendasAccesibles) {
  if (!scope || scope.isAll) return true;
  if (!clienteId || !Array.isArray(tiendasAccesibles)) return false;
  const idStr = String(clienteId);
  return tiendasAccesibles.some((t) => t && String(t.cliente) === idStr);
}

/**
 * ¿Tiene el usuario alguna restricción de scope efectiva?
 * Útil para decidir si lanzar consultas filtradas o no.
 */
export function hasScopeRestriction(scope) {
  if (!scope || scope.isAll) return false;
  return scope.inspectorCods.length > 0
    || scope.delegaciones.length > 0
    || scope.empresas.length > 0
    || scope.departamentos.length > 0;
}

export const ROLE_ADMIN = ADMIN;
export const ROLE_ADMINISTRATIVO = ADMINISTRATIVO;
export const ROLE_INSPECTOR = INSPECTOR;
